// Selection state for the peek panel, and the small per-device toggles that go with
// a table view.
//
// The selected row lives in the URL rather than in component state: the panel is a
// view of a record, and a view of a record is a place. That buys the back button
// closing it, a reload restoring it, and a link to "this list, with that row open".

import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export function usePeek() {
  const [params, setParams] = useSearchParams()
  const peek = params.get('peek') ?? undefined

  const openPeek = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params)
      // Clicking the open row again closes it, which is what a toggle should do and
      // saves a trip to the ✕.
      if (next.get('peek') === id) next.delete('peek')
      else next.set('peek', id)
      // `replace` so a triage session doesn't bury the page you came from under
      // twenty history entries.
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  return { peek, openPeek }
}

/**
 * A boolean the device remembers. For view preferences only — how you like a list
 * shaped is yours, not the workspace's, so it never goes in the log.
 */
export function useStickyToggle(key: string, fallback: boolean) {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw == null ? fallback : raw === '1'
    } catch {
      return fallback
    }
  })
  const set = useCallback(
    (next: boolean) => {
      setValue(next)
      try {
        localStorage.setItem(key, next ? '1' : '0')
      } catch {
        /* losing a view pref costs nothing */
      }
    },
    [key],
  )
  return [value, set] as const
}

/**
 * Its twin, for a preference with more than two states — which period the deal
 * numbers are scoped to. Same contract: the device remembers it, the log never
 * sees it, and losing it costs nothing.
 */
export function useStickyValue(key: string, fallback: string) {
  const [value, setValue] = useState<string>(() => {
    try {
      return localStorage.getItem(key) ?? fallback
    } catch {
      return fallback
    }
  })
  const set = useCallback(
    (next: string) => {
      setValue(next)
      try {
        localStorage.setItem(key, next)
      } catch {
        /* losing a view pref costs nothing */
      }
    },
    [key],
  )
  return [value, set] as const
}
